import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import { useNavigate } from "react-router-dom";
import { StatusBadge } from "./StatusBadge";
import { api, API, fileUrl } from "../lib/api";
import { PLATFORM_COLORS, REACTIONS, videoThumbnail, videoJumpUrl, DISCORD_INVITE } from "../lib/constants";
import { useAuth } from "../context/AuthContext";
import { CaseComments } from "./CaseComments";
import { UserBadges } from "./UserBadges";
import { StreamerBadges, STREAMER_PLATFORM_KEYS, STREAMER_PLATFORM_META } from "./StreamerBadges";
import { InfoTip } from "./InfoTip";
import { ClipEvidence } from "./ClipEvidence";
import { CrewMap } from "./CrewMap";
import { LinkTeammate } from "./LinkTeammate";
import { DispatchCountdown } from "./DispatchCountdown";
import { ChevronUp, ChevronDown, Video, ExternalLink, CalendarDays, UserCircle2, Repeat, ShieldQuestion, Trash2, Gavel, Ban, RefreshCw, Check, X, Crosshair, Radio, Info, Radar, ShieldOff, Clock, Eye, Sparkles, Loader2, Flame, Users, MessageCircle } from "lucide-react";
import { toast } from "sonner";

const TRACKABLE_PLATFORMS = ["Steam", "Xbox", "PS5"];

export const ReportDetailModal = ({ report, open, onClose, onUpvote, onDownvote, onReact, onDelete, onEvidenceSaved }) => {
  const { user, isMod, openAuth } = useAuth();
  const navigate = useNavigate();
  const [banFlags, setBanFlags] = useState(0);
  const [iFlagged, setIFlagged] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [newName, setNewName] = useState("");
  const [aka, setAka] = useState([]);
  const [trackedName, setTrackedName] = useState("");
  const [streamers, setStreamers] = useState([]);
  const [evidence, setEvidence] = useState([]);
  const [scanStatus, setScanStatus] = useState(null);
  const [aiSummary, setAiSummary] = useState(null);
  const [runsWith, setRunsWith] = useState([]);
  const [crewKey, setCrewKey] = useState(0);

  useEffect(() => {
    setBanFlags(report?.ban_flags || 0);
    setIFlagged(!!report?.i_flagged_banned);
    setAka(report?.aka || []);
    setTrackedName(report?.tracked_name || report?.gamertag || "");
    setStreamers(report?.streamer_platforms || []);
    setEvidence(report?.video_evidence || []);
    setScanStatus(report?.ai_scan_status || null);
    setAiSummary(report?.ai_summary || null);
    setRunsWith(report?.runs_with || []);
    setRenaming(false);
    setNewName("");
  }, [report?.id, report?.ban_flags, report?.i_flagged_banned]);

  // Live-refresh while the background AI is still analyzing this case's clips.
  useEffect(() => {
    if (!open || !report?.id || scanStatus !== "pending") return;
    let tries = 0;
    const iv = setInterval(async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/reports/${report.id}`);
        setEvidence(data.video_evidence || []);
        setAiSummary(data.ai_summary || null);
        if (data.ai_scan_status !== "pending") { setScanStatus(data.ai_scan_status || "done"); clearInterval(iv); }
      } catch { /* ignore */ }
      if (tries >= 24) { setScanStatus("done"); clearInterval(iv); }
    }, 5000);
    return () => clearInterval(iv);
  }, [open, report?.id, scanStatus]);

  if (!report) return null;
  const platColor = PLATFORM_COLORS[report.platform] || "#94A3B8";
  const isSuspect = report.status === "suspect" || report.status === "disputed";
  const isConfirmed = report.status === "banned" || report.status === "confirmed_cheater";
  const isTracked = !!report.platform_uid;
  const isTrackable = TRACKABLE_PLATFORMS.includes(report.platform);

  const flagBanned = async () => {
    if (!user) return openAuth();
    try {
      const { data } = await api.post(`/reports/${report.id}/report-banned`);
      setBanFlags(data.ban_flags);
      setIFlagged(data.i_flagged_banned);
    } catch { /* ignore */ }
  };

  const logNameChange = async () => {
    const nn = newName.trim();
    if (!nn) return;
    try {
      await api.post(`/admin/name-change`, {
        platform: report.platform,
        old_gamertag: trackedName || report.gamertag,
        new_gamertag: nn,
      });
      setAka((a) => [...a, { from: trackedName || report.gamertag, to: nn, at: new Date().toISOString(), source: `manual:${user?.name}` }]);
      setTrackedName(nn);
      setRenaming(false);
      setNewName("");
      toast.success(`Logged name change → "${nn}". Case stays pinned.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not log the name change.");
    }
  };

  const handleUpvote = () => {
    if (!user) return openAuth();
    onUpvote(report);
  };

  const handleDownvote = () => {
    if (!user) return openAuth();
    onDownvote && onDownvote(report);
  };

  const toggleStreamer = async (plat) => {
    const next = streamers.includes(plat) ? streamers.filter((p) => p !== plat) : [...streamers, plat];
    setStreamers(next);
    try {
      await api.patch(`/reports/${report.id}/streamer`, { platforms: next });
    } catch {
      toast.error("Could not update the streamer label.");
      setStreamers(streamers);
    }
  };

  const unlinkTeammate = async (t) => {
    try {
      const { data } = await api.post(`/reports/${report.id}/unlink-teammate`, { platform: t.platform, gamertag: t.gamertag });
      setRunsWith(data.runs_with || []);
      setCrewKey((k) => k + 1);
      toast.success(`Unlinked ${t.gamertag} from ${report.gamertag}.`, data.unlinked_report_id ? {
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              const { data: r } = await api.post(`/reports/${report.id}/link-teammate`, { teammate_report_ids: [data.unlinked_report_id] });
              setRunsWith(r.runs_with || []);
              setCrewKey((k) => k + 1);
              toast.success(`Re-linked ${t.gamertag}.`);
            } catch { toast.error("Could not undo the unlink."); }
          },
        },
      } : undefined);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not unlink teammate.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent hideClose className="max-h-[90vh] overflow-y-auto border-[#1E293F] bg-[#0F1424] p-0 text-white sm:max-w-2xl" data-testid="report-detail-modal">
        <DialogTitle className="sr-only">Cheater report for {report.gamertag}</DialogTitle>
        <div className="sticky top-0 z-30 flex items-center justify-between gap-3 border-b border-[#1E293F] bg-[#0F1424]/95 px-4 py-2.5 backdrop-blur">
          <span className="text-xs font-semibold uppercase tracking-wider text-[#64748B]">Case File</span>
          <button onClick={onClose} data-testid="modal-close-btn" aria-label="Close"
            className="inline-flex items-center gap-1.5 rounded-full border border-[#1E293F] bg-[#141B2D] px-3.5 py-1.5 text-sm font-bold text-[#94A3B8] transition-colors hover:border-[#FF2A55]/60 hover:bg-[#FF2A55]/10 hover:text-[#FF2A55]">
            <X size={18} /> Close
          </button>
        </div>
        <div className="p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="mb-2"><StatusBadge status={report.status} testid="detail-status" /></div>
              <h2 className="font-heading text-3xl font-black tracking-tight text-white" data-testid="detail-gamertag">{report.gamertag}</h2>
              {trackedName && trackedName !== report.gamertag && (
                <div className="mt-1 flex items-center gap-1.5 text-sm font-semibold text-[#F97316]" data-testid="detail-now-known-as">
                  <RefreshCw size={13} /> now goes by <span className="text-white">{trackedName}</span>
                </div>
              )}
              <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
                <span className="rounded px-2 py-0.5 font-mono font-semibold" style={{ color: platColor, backgroundColor: `${platColor}18` }}>{report.platform}</span>
                <span className="rounded bg-[#141B2D] px-2 py-0.5 text-[#94A3B8]">{report.game_mode}</span>
                <span className="rounded border border-[#FF2A55]/30 bg-[#FF2A55]/10 px-2 py-0.5 font-semibold text-[#FF2A55]">{report.cheat_type}</span>
                {report.is_serial && (
                  <span className="inline-flex items-center gap-1 rounded bg-[#FF2A55] px-2 py-0.5 text-xs font-bold uppercase text-white"><Repeat size={11} /> Serial</span>
                )}
                {isTracked ? (
                  <span className="inline-flex items-center gap-1">
                    <span data-testid="detail-tracking-badge" className="inline-flex items-center gap-1 rounded bg-[#00FF87] px-2 py-0.5 text-xs font-bold uppercase text-[#062B1A]"><Radar size={11} /> Tracking</span>
                    <InfoTip testid="detail-tracking-info" side="bottom" text="This cheater is locked to their permanent platform ID (Steam/Xbox/PSN). Our tracker auto-checks their current name every hour — if they rename to dodge the wall, the case updates automatically." />
                  </span>
                ) : isTrackable ? (
                  <span className="inline-flex items-center gap-1">
                    <span data-testid="detail-not-linked-badge" className="inline-flex items-center gap-1 rounded border border-[#334155] bg-[#1E293F] px-2 py-0.5 text-xs font-bold uppercase text-[#94A3B8]"><ShieldOff size={11} /> Not linked</span>
                    <InfoTip testid="detail-not-linked-info" side="bottom" text="No permanent platform ID was captured for this case, so we can't auto-detect if they rename. A mod can still log a name change manually." />
                  </span>
                ) : null}
              </div>
              {runsWith?.length > 0 && (
                <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid="detail-runs-with">
                  <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-[#C084FC]"><Users size={12} /> Runs with</span>
                  {runsWith.map((t, i) => (
                    <span key={i} className="inline-flex items-center gap-0.5 rounded-full border border-[#7C3AED]/40 bg-[#7C3AED]/10 pl-2 pr-1 py-0.5 text-xs font-semibold text-[#C084FC]">
                      <button data-testid={`runs-with-link-${i}`}
                        onClick={() => { onClose(); navigate(`/players/${encodeURIComponent(t.platform)}/${encodeURIComponent(t.gamertag)}`); }}
                        className="transition-colors hover:text-white">
                        {t.gamertag}
                      </button>
                      {isMod && (
                        <button data-testid={`runs-with-unlink-${i}`} title={`Unlink ${t.gamertag}`}
                          onClick={() => unlinkTeammate(t)}
                          className="rounded-full p-0.5 text-[#94A3B8] transition-colors hover:bg-[#FF2A55]/20 hover:text-[#FF2A55]">
                          <X size={11} />
                        </button>
                      )}
                    </span>
                  ))}
                  <InfoTip testid="runs-with-info" side="bottom" text="These accounts were caught cheating in the same clip — proof they play together. Click one to open their case." />
                </div>
              )}
            </div>
            {isSuspect ? (
              <div className="flex flex-col items-center gap-1.5">
                <button
                  data-testid="detail-upvote-btn"
                  onClick={handleUpvote}
                  disabled={report.has_upvoted}
                  title={report.has_upvoted ? "You've already marked this — one vote per person" : "I faced this cheater"}
                  className={`flex w-14 flex-col items-center rounded-lg border px-2 py-1.5 transition-all ${
                    report.has_upvoted ? "border-[#FF2A55] bg-[#FF2A55]/15 text-[#FF2A55] cursor-default" : "border-[#1E293F] bg-[#141B2D] text-[#94A3B8] hover:border-[#FF2A55]/50 hover:text-[#FF2A55]"
                  }`}
                >
                  <ChevronUp size={16} strokeWidth={2.5} />
                  <span className="text-sm font-bold font-mono">{report.upvotes}</span>
                  <span className="text-[8px] uppercase tracking-wide">faced him</span>
                </button>
                <button
                  data-testid="detail-downvote-btn"
                  onClick={handleDownvote}
                  className={`flex w-14 flex-col items-center rounded-lg border px-2 py-1.5 transition-all ${
                    report.has_downvoted ? "border-[#00FF87] bg-[#00FF87]/15 text-[#00FF87]" : "border-[#1E293F] bg-[#141B2D] text-[#64748B] hover:border-[#00FF87]/50 hover:text-[#00FF87]"
                  }`}
                >
                  <span className="text-[8px] uppercase tracking-wide">legit</span>
                  <span className="text-sm font-bold font-mono">{report.downvotes}</span>
                  <ChevronDown size={16} strokeWidth={2.5} />
                </button>
              </div>
            ) : (
              <button
                data-testid="detail-upvote-btn"
                onClick={handleUpvote}
                disabled={report.has_upvoted}
                title={report.has_upvoted ? "You've already marked this — one vote per person" : "I faced this cheater"}
                className={`flex flex-col items-center rounded-lg border px-3 py-2 transition-all ${
                  report.has_upvoted ? "border-[#FF2A55] bg-[#FF2A55]/15 text-[#FF2A55] cursor-default" : "border-[#1E293F] bg-[#141B2D] text-[#94A3B8] hover:border-[#FF2A55]/50 hover:text-[#FF2A55]"
                }`}
              >
                <ChevronUp size={18} strokeWidth={2.5} />
                <span className="text-sm font-bold font-mono">{report.upvotes}</span>
                <span className="text-[9px] uppercase tracking-wide">faced him</span>
              </button>
            )}
          </div>

          {report.is_demo && (
            <div className="mt-4 flex items-start gap-2.5 rounded-lg border border-[#00F0FF]/35 bg-[#00F0FF]/[0.08] p-3.5" data-testid="detail-demo-note">
              <Info size={17} className="mt-px shrink-0 text-[#00F0FF]" />
              <p className="text-sm leading-relaxed text-[#7DD3FC]">
                <strong className="text-white">This is a sample case.</strong> It's here to demonstrate what a real report looks like — the evidence, the community voting, reactions and discussion you get when you report a cheater. <strong className="text-white">Not an actual player.</strong>
              </p>
            </div>
          )}

          {isSuspect && (
            <div className="mt-4 flex items-start gap-2 rounded-lg border border-[#F97316]/30 bg-[#F97316]/[0.08] p-3 text-xs leading-relaxed text-[#F59E0B]" data-testid="detail-unverified-note">
              <ShieldQuestion size={16} className="mt-px shrink-0" />
              <span>This is a community post on a <strong>suspected</strong> cheater. It is <strong>not confirmed or vetted by moderators.</strong> Upvote if you've faced them, or press "legit" if you think they're wrongly listed.</span>
            </div>
          )}

          <p className="mt-5 rounded-lg border border-[#1E293F] bg-[#141B2D] p-4 text-sm leading-relaxed text-[#CBD5E1]" data-testid="detail-description">
            {report.description}
          </p>

          {report.verdict_note && (
            <div className="mt-4 rounded-lg border border-[#00FF87]/30 bg-[#00FF87]/[0.08] p-4" data-testid="detail-verdict">
              <h4 className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#4ADE80]"><Gavel size={13} /> Modder Verdict</h4>
              <p className="mt-1.5 whitespace-pre-wrap text-sm leading-relaxed text-[#BBF7D0]">{report.verdict_note}</p>
            </div>
          )}

          {isConfirmed && report.reporter_name && (
            <div className="mt-4 flex flex-wrap items-center gap-2.5 rounded-lg border border-[#00F0FF]/30 bg-[#00F0FF]/[0.07] p-3" data-testid="detail-busted-by">
              <Crosshair size={16} className="shrink-0 text-[#00F0FF]" />
              <p className="text-sm text-[#7DD3FC]">
                Busted by{" "}
                <button
                  onClick={() => { if (report.reported_by) { onClose(); navigate(`/hunters/${report.reported_by}`); } }}
                  className="font-bold text-white hover:underline"
                  data-testid="detail-busted-by-link"
                >{report.reporter_name}</button>{" "}
                — a Certified Hunter who called this cheater out.
              </p>
              <StreamerBadges platforms={streamers} size={15} />
            </div>
          )}

          {isMod && (
            <div className="mt-4 rounded-lg border border-[#00FF87]/25 bg-[#00FF87]/[0.06] p-3" data-testid="streamer-mod-toggle">
              <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#86EFAC]">
                <Radio size={13} /> Streamer label <span className="font-normal normal-case text-[#94A3B8]">(mod only · shows on confirmed cases, no links)</span>
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                {STREAMER_PLATFORM_KEYS.map((p) => {
                  const m = STREAMER_PLATFORM_META[p];
                  const on = streamers.includes(p);
                  return (
                    <button
                      key={p}
                      data-testid={`streamer-toggle-${p}`}
                      onClick={() => toggleStreamer(p)}
                      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-all ${on ? "text-white" : "border-[#1E293F] bg-[#141B2D] text-[#94A3B8] hover:text-white"}`}
                      style={on ? { borderColor: m.color, backgroundColor: `${m.color}26` } : {}}
                    >
                      <svg viewBox="0 0 24 24" width={13} height={13} fill={on ? m.color : "#94A3B8"}><path d={m.path} /></svg>
                      {m.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {isMod && (
            <div className="mt-4 flex flex-wrap items-center gap-2 rounded-lg border border-[#00F0FF]/25 bg-[#00F0FF]/[0.06] p-3" data-testid="mod-dossier-box">
              <span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#00F0FF]"><Gavel size={13} /> Report to 2K</span>
              <span className="text-xs text-[#94A3B8]">One-click printable dossier — clips, shot chart & stats.</span>
              {report.dispatch_status && <DispatchCountdown status={report.dispatch_status} />}
              <a href={`${API}/reports/${report.id}/dossier.pdf`} target="_blank" rel="noreferrer" data-testid="export-dossier-btn"
                className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-[#00F0FF]/40 bg-[#00F0FF]/10 px-3 py-1.5 text-sm font-bold text-[#00F0FF] transition-colors hover:bg-[#00F0FF]/20">
                <ExternalLink size={14} /> Export dossier (PDF)
              </a>
              {runsWith?.length > 0 && (
                <a href={`${API}/reports/${report.id}/dossier.pdf?team=1`} target="_blank" rel="noreferrer" data-testid="export-team-dossier-btn"
                  className="inline-flex items-center gap-1.5 rounded-lg border border-[#7C3AED]/40 bg-[#7C3AED]/10 px-3 py-1.5 text-sm font-bold text-[#C084FC] transition-colors hover:bg-[#7C3AED]/20">
                  <Users size={14} /> Team dossier
                </a>
              )}
            </div>
          )}

          {isMod && <LinkTeammate reportId={report.id} currentGamertag={report.gamertag} onLinked={(u) => { setRunsWith(u.runs_with || []); setCrewKey((k) => k + 1); }} />}

          {runsWith?.length > 0 && <CrewMap key={crewKey} reportId={report.id} onNavigate={onClose} />}

          {isConfirmed && (
            <div className="mt-4 flex flex-wrap items-center gap-3 rounded-lg border border-[#5865F2]/30 bg-[#5865F2]/[0.08] p-3" data-testid="appeal-box">
              <ShieldQuestion size={16} className="shrink-0 text-[#8B93F8]" />
              <p className="flex-1 text-xs leading-relaxed text-[#C7CBF5]">
                <span className="font-semibold text-white">Listed by mistake?</span> You can appeal on our Discord with a handcam clip{report.dispatch_status?.state === "pending" ? <> — you have <span className="font-semibold text-white">{report.dispatch_status.days_left} day{report.dispatch_status.days_left === 1 ? "" : "s"}</span> before this goes to 2K.</> : " before this is escalated to 2K."}
              </p>
              <a href={DISCORD_INVITE} target="_blank" rel="noreferrer" data-testid="appeal-discord-btn"
                className="inline-flex items-center gap-1.5 rounded-lg bg-[#5865F2] px-3 py-1.5 text-sm font-bold text-white transition-colors hover:bg-[#4752C4]">
                <MessageCircle size={14} /> Open appeal on Discord
              </a>
              <button onClick={() => { onClose(); navigate("/faq#appeal"); }} data-testid="appeal-rules-link"
                className="text-xs font-semibold text-[#8B93F8] transition-colors hover:text-white hover:underline">
                Appeal rules
              </button>
            </div>
          )}

          {isConfirmed && (
            <div className={`mt-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3 ${banFlags >= (report.ban_confirm_threshold || 3) ? "border-[#00FF87]/50 bg-[#00FF87]/[0.12]" : "border-[#00FF87]/25 bg-[#00FF87]/[0.06]"}`} data-testid="ban-confirm-box">
              <div className="flex items-start gap-2 text-xs leading-relaxed text-[#86EFAC]">
                <Ban size={16} className="mt-px shrink-0" />
                {banFlags >= (report.ban_confirm_threshold || 3) ? (
                  <span data-testid="ban-confirmed-ribbon"><strong>Confirmed banned by the community.</strong> {banFlags} players caught this account banned in-game. The wall works. 🔒</span>
                ) : (
                  <span>Caught this player <strong>banned in-game</strong> by 2K? Confirm it so others know the report landed. <strong>{banFlags}</strong> {banFlags === 1 ? "player has" : "players have"} confirmed the ban.</span>
                )}
              </div>
              <button
                data-testid="ban-confirm-btn"
                onClick={flagBanned}
                className={`shrink-0 inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-bold transition-all ${
                  iFlagged ? "border-[#00FF87] bg-[#00FF87]/20 text-[#00FF87]" : "border-[#00FF87]/40 bg-transparent text-[#86EFAC] hover:bg-[#00FF87]/10"
                }`}
              >
                <Ban size={14} /> {iFlagged ? "Ban confirmed" : "I saw them banned"}
              </button>
            </div>
          )}

          {(aka.length > 0 || isMod) && (
            <div className="mt-4 rounded-lg border border-[#F97316]/25 bg-[#F97316]/[0.06] p-3" data-testid="alias-tracker">
              <div className="flex items-center justify-between gap-2">
                <h4 className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#F97316]"><RefreshCw size={13} /> Alias Tracker</h4>
                {isMod && !renaming && (
                  <button data-testid="log-name-change-btn" onClick={() => setRenaming(true)}
                    className="rounded-md border border-[#F97316]/40 px-2 py-1 text-xs font-semibold text-[#F97316] transition-colors hover:bg-[#F97316]/15">
                    + Log name change
                  </button>
                )}
              </div>
              {aka.length > 0 ? (
                <ul className="mt-2 space-y-1 text-xs text-[#CBD5E1]" data-testid="alias-history">
                  {aka.map((a, i) => (
                    <li key={i} className="flex flex-wrap items-center gap-1.5">
                      <span className="text-[#94A3B8]">{new Date(a.at).toLocaleDateString()}</span>
                      <span className="rounded bg-[#141B2D] px-1.5 py-0.5 line-through decoration-[#64748B]">{a.from}</span>
                      <RefreshCw size={11} className="text-[#F97316]" />
                      <span className="rounded bg-[#F97316]/15 px-1.5 py-0.5 font-semibold text-[#FDBA74]">{a.to}</span>
                      {a.source?.startsWith("auto") && <span className="rounded bg-[#00F0FF]/15 px-1 text-[9px] font-bold uppercase text-[#00F0FF]">auto</span>}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-1.5 text-xs text-[#94A3B8]">No name changes detected. {isTracked ? "This case is locked to a permanent platform ID — we auto-check the current name every hour." : isTrackable ? "No permanent ID was captured for this case, so auto-detection is off. Log a rename manually if they dodge the wall." : "Log one manually if they rename to dodge the wall."}</p>
              )}
              {isMod && renaming && (
                <div className="mt-2 flex items-center gap-2" data-testid="rename-form">
                  <input
                    data-testid="rename-input"
                    autoFocus
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && logNameChange()}
                    placeholder="New gamertag / name"
                    className="flex-1 rounded-lg border border-[#1E293F] bg-[#141B2D] px-3 py-1.5 text-sm text-white placeholder:text-[#64748B] focus:border-[#F97316]/50 focus:outline-none"
                  />
                  <button data-testid="rename-confirm" onClick={logNameChange} disabled={!newName.trim()}
                    className="rounded-lg bg-[#F97316] p-2 text-white disabled:opacity-50"><Check size={15} /></button>
                  <button data-testid="rename-cancel" onClick={() => { setRenaming(false); setNewName(""); }}
                    className="rounded-lg border border-[#1E293F] bg-[#141B2D] p-2 text-[#94A3B8]"><X size={15} /></button>
                </div>
              )}
            </div>
          )}

          {onReact && (
            <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="reaction-bar">
              <span className="text-xs text-[#64748B]">React:</span>
              {REACTIONS.map((e) => {
                const count = report.reaction_counts?.[e] || 0;
                const mine = report.my_reactions?.includes(e);
                return (
                  <button
                    key={e}
                    data-testid={`react-${e}`}
                    onClick={() => onReact(report, e)}
                    className={`flex items-center gap-1 rounded-full border px-3 py-1.5 text-sm transition-all ${
                      mine ? "border-[#00F0FF] bg-[#00F0FF]/15 text-white" : "border-[#1E293F] bg-[#141B2D] text-[#94A3B8] hover:border-[#00F0FF]/40 hover:text-white"
                    }`}
                  >
                    <span>{e}</span>{count > 0 && <span className="font-mono text-xs">{count}</span>}
                  </button>
                );
              })}
            </div>
          )}

          {report.images?.length > 0 && (
            <div className="mt-5">
              <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-[#64748B]">Photo Evidence</h4>
              <div className="grid grid-cols-2 gap-3">
                {report.images.map((img, i) => (
                  <a key={i} href={fileUrl(img)} target="_blank" rel="noreferrer" className="group relative overflow-hidden rounded-lg border border-[#1E293F]">
                    <img src={fileUrl(img)} alt={`evidence ${i + 1}`} className="h-40 w-full object-cover transition-transform group-hover:scale-105" />
                  </a>
                ))}
              </div>
            </div>
          )}

          {report.video_links?.length > 0 && (
            <div className="mt-5">
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-[#64748B]">Video Proof</h4>
                {aiSummary && aiSummary.greens > 0 && (
                  <span data-testid="ai-green-summary" className="inline-flex items-center gap-1.5 rounded-full border border-[#00FF87]/30 bg-[#00FF87]/10 px-2.5 py-0.5 text-[11px] font-bold text-[#00FF87]">
                    <Sparkles size={11} /> AI found {aiSummary.greens} green{aiSummary.greens === 1 ? "" : "s"}
                    {aiSummary.contested > 0 && (
                      <span className="ml-1 rounded-full bg-[#FF2A55]/20 px-1.5 py-0.5 text-[#FF6B8A]" data-testid="ai-contested-count">{aiSummary.contested} contested</span>
                    )}
                  </span>
                )}
                {aiSummary && aiSummary.green_streak && (
                  <span data-testid="ai-green-streak" title="The AI found an unusually high rate of contested greens on this player — a strong macro/auto-green signal." className="inline-flex items-center gap-1.5 rounded-full border border-[#FF2A55]/40 bg-[#FF2A55]/15 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider text-[#FF6B8A]">
                    <Flame size={11} /> Green Streak
                  </span>
                )}
                {scanStatus === "pending" && (
                  <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#C084FC]"><Loader2 size={11} className="animate-spin" /> AI analyzing…</span>
                )}
              </div>
              <div className="space-y-3">
                {report.video_links.map((v, i) => (
                  <ClipEvidence
                    key={v}
                    index={i}
                    reportId={report.id}
                    url={v}
                    gamertag={report.gamertag}
                    evidence={(evidence || []).find((e) => e.url === v) || null}
                    isMod={isMod}
                    scanPending={scanStatus === "pending"}
                    isDemo={!!report.is_demo}
                    onSaved={(updated) => { setEvidence(updated.video_evidence || []); onEvidenceSaved && onEvidenceSaved(updated); }}
                  />
                ))}
              </div>
            </div>
          )}

          <div className="mt-6 flex flex-wrap items-center gap-4 border-t border-[#1E293F] pt-4 text-xs text-[#64748B]">
            <span className="flex items-center gap-1.5"><UserCircle2 size={14} /> {report.reporter_name}</span>
            <UserBadges supporter={report.reporter_supporter} tipper={report.reporter_tipper} />
            <span className="flex items-center gap-1.5"><CalendarDays size={14} /> {new Date(report.created_at).toLocaleDateString()}</span>
            {report.vetted_by && <span className="flex items-center gap-1.5 text-[#00F0FF]">Vetted by {report.vetted_by}</span>}
            <button
              data-testid="detail-player-link"
              onClick={() => { onClose(); navigate(`/players/${report.platform}/${encodeURIComponent(report.gamertag)}`); }}
              className="ml-auto flex items-center gap-1 font-semibold text-[#00F0FF] hover:underline"
            >
              View full player history <ExternalLink size={12} />
            </button>
          </div>

          {isMod && onDelete && (
            <div className="mt-4 flex justify-end border-t border-[#1E293F] pt-4">
              <button
                data-testid="detail-delete-btn"
                onClick={() => onDelete(report)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-[#FF2A55]/40 bg-[#FF2A55]/10 px-3 py-1.5 text-sm font-semibold text-[#FF2A55] transition-colors hover:bg-[#FF2A55]/20"
              >
                <Trash2 size={14} /> Delete this post
              </button>
            </div>
          )}

          <CaseComments reportId={report.id} />
        </div>
      </DialogContent>
    </Dialog>
  );
};
